import { ReactNode, useMemo, useState } from 'react'
import { createColumnHelper } from '@tanstack/react-table'
import {
  PiCaretLeft,
  PiCaretRight,
  PiCheckCircle,
  PiCircleNotch,
  PiFlag,
  PiShieldWarning,
  PiUsers,
  PiXCircle,
} from 'react-icons/pi'
import { toast } from 'react-toastify'

import { DataTable, DataTableProvider } from 'components/DataTable'
import { DataTableContainer } from 'components/DataTable/UI'
import { Alert } from 'components/FbUI/Alert'
import FbButton from 'components/FbUI/FbButton'
import { PillTabs } from 'components/FbUI/PillTabs'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { HelpTooltip } from 'components/Tooltip/HelpTooltip'
import { cn } from 'components/UI/cn'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import {
  CampaignLeadReviewItem,
  useActiveCampaignValidationJob,
  useGetCampaignLeadReview,
  useStartCampaignValidationJob,
  useUpdateCampaignLeadReview,
  useValidateContactEmails,
} from 'features/campaigns/campaigns_api'
import { useSyncCampaignLeads } from 'features/campaigns/leads_api'
import { useApplyEmailOverrides } from 'features/campaigns/email_overrides_api'
import { queryClient } from 'services/queryClient'

import type { Campaign } from 'models/campaign'

import { WizardStepFooter, WIZARD_BUTTON_MIN_W } from './WizardStepFooter'
import { WizardStepHeader } from './WizardStepHeader'

interface Props {
  campaignId: number
  bisonCampaignId?: number
  onBack: () => void
  onNext: () => void
  stepNumber: number
  totalSteps: number
  isLiveCampaign?: boolean
  campaign?: Campaign
}

type LeadReviewFilter =
  | 'all'
  | 'included'
  | 'excluded'
  | 'flagged'
  | 'needs_validation'

const EMPTY_STATE_BY_FILTER: Record<
  LeadReviewFilter,
  { title: string; subtitle: string }
> = {
  included: {
    title: 'No included contacts.',
    subtitle: 'Adjust your review filters or campaign contacts to see results.',
  },
  excluded: {
    title: 'No excluded contacts.',
    subtitle: 'Contacts you exclude from this campaign will appear here.',
  },
  flagged: {
    title: 'No flagged contacts.',
    subtitle:
      'Contacts that need review (e.g. personal email, existing deal, prior campaign history) will appear here.',
  },
  needs_validation: {
    title: 'No contacts need validation.',
    subtitle:
      'Contacts whose email deliverability has not been verified will appear here.',
  },
  all: {
    title: 'No contacts found.',
    subtitle: 'Adjust your review filters or campaign contacts to see results.',
  },
}

const columnHelper = createColumnHelper<CampaignLeadReviewItem>()

const SUMMARY_TONES = {
  approved: {
    Icon: PiCheckCircle,
    iconSquare: 'bg-success-bg-subtle text-success-strong',
  },
  flagged: {
    Icon: PiFlag,
    iconSquare: 'bg-warning-bg-subtle text-warning-strong',
  },
  needs_validation: {
    Icon: PiShieldWarning,
    iconSquare: 'bg-info-bg-subtle text-info-strong',
  },
  rejected: {
    Icon: PiXCircle,
    iconSquare: 'bg-error-bg-subtle text-error-strong',
  },
  neutral: {
    Icon: PiUsers,
    iconSquare: 'bg-neutral-100 text-neutral-600',
  },
} as const

function SummaryCard({
  label,
  value,
  tone,
  tooltip,
}: {
  label: string
  value: number
  tone: keyof typeof SUMMARY_TONES
  tooltip?: string
}) {
  const { Icon, iconSquare } = SUMMARY_TONES[tone]

  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          'flex size-7 shrink-0 items-center justify-center rounded-lg',
          iconSquare
        )}
      >
        <Icon className="size-3.5" />
      </span>
      <span className="text-base font-bold leading-none tabular-nums text-fg">
        {value}
      </span>
      <span className="whitespace-nowrap text-base font-normal leading-none text-fg-secondary">
        {label}
      </span>
      {tooltip ? <HelpTooltip content={tooltip} /> : null}
    </div>
  )
}

// Mirror of the backend EmailValidity enum (server/contacts/models.py).
const EMAIL_VALIDITY_INVALID = 0
const EMAIL_VALIDITY_VALID = 1
const EMAIL_VALIDITY_CATCH_ALL = 3
const EMAIL_VALIDITY_UNKNOWN = 4
const EMAIL_VALIDITY_SPAMTRAP = 5
const EMAIL_VALIDITY_ABUSE = 6
const EMAIL_VALIDITY_DO_NOT_MAIL = 7

function ValidationBadge({
  validity,
  loading = false,
}: {
  validity: CampaignLeadReviewItem['email_validity']
  loading?: boolean
}) {
  if (loading) {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-surface-alt px-2 py-0.5 text-xs font-medium text-fg-secondary">
        <PiCircleNotch className="h-3 w-3 shrink-0 animate-spin" />
        Validating
      </span>
    )
  }
  if (validity == null) {
    return (
      <span className="inline-flex rounded-full bg-surface-alt px-2 py-0.5 text-xs font-medium text-fg-secondary">
        Not validated
      </span>
    )
  }
  if (validity === EMAIL_VALIDITY_VALID) {
    return (
      <span className="inline-flex rounded-full bg-success-bg-subtle px-2 py-0.5 text-xs font-medium text-success-strong">
        Valid
      </span>
    )
  }
  if (validity === EMAIL_VALIDITY_CATCH_ALL) {
    return (
      <span className="inline-flex rounded-full bg-success-bg-subtle px-2 py-0.5 text-xs font-medium text-success-strong">
        Catch-all
      </span>
    )
  }
  if (validity === EMAIL_VALIDITY_UNKNOWN) {
    return (
      <span className="inline-flex rounded-full bg-warning-bg px-2 py-0.5 text-xs font-medium text-fg">
        Unknown
      </span>
    )
  }
  if (validity === EMAIL_VALIDITY_INVALID) {
    return (
      <span className="inline-flex rounded-full bg-error-bg-subtle px-2 py-0.5 text-xs font-medium text-error-strong">
        Invalid
      </span>
    )
  }
  if (
    validity === EMAIL_VALIDITY_SPAMTRAP ||
    validity === EMAIL_VALIDITY_ABUSE ||
    validity === EMAIL_VALIDITY_DO_NOT_MAIL
  ) {
    return (
      <span className="inline-flex rounded-full bg-error-bg-subtle px-2 py-0.5 text-xs font-medium text-error-strong">
        Risky
      </span>
    )
  }
  return null
}

function LeadStatusBadge({
  qaStatus,
}: {
  qaStatus: CampaignLeadReviewItem['qa_status']
}) {
  const isApproved = qaStatus === 'approved'
  const isFlagged = qaStatus === 'flagged'
  const isNeedsValidation = qaStatus === 'needs_validation'
  const isRejected = qaStatus === 'rejected'

  return (
    <span
      className={cn(
        'inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium capitalize',
        isApproved && 'bg-success-bg-subtle text-success-strong',
        isFlagged && 'bg-warning-bg text-fg',
        isNeedsValidation && 'bg-info-bg-subtle text-info-strong',
        isRejected && 'bg-error-bg-subtle text-error-strong'
      )}
    >
      {isNeedsValidation ? 'Needs validation' : qaStatus}
    </span>
  )
}

function filterTabTitle(label: string, count: number) {
  return (
    <span className="flex items-center gap-1.5">
      {label}
      <span className="text-xs text-inherit/70">{count}</span>
    </span>
  )
}

function WizardReviewLeadsStepContent({
  campaignId,
  bisonCampaignId = 0,
  onBack,
  onNext,
  stepNumber,
  totalSteps,
  isLiveCampaign = false,
  campaign,
}: Props) {
  const [filter, setFilter] = useState<LeadReviewFilter>('included')
  const [activeTab, setActiveTab] = useState<'in-sequence' | 'new-contacts'>(
    'in-sequence'
  )
  const [isSyncing, setIsSyncing] = useState(false)
  const [validatingContactIds, setValidatingContactIds] = useState<Set<number>>(
    () => new Set()
  )
  // Fast bulk jobs (<1s) can complete before the 2s validation-job poller
  // ever observes a pending/in_progress state — without this, the spinner
  // never paints. Hold the running state on for at least 2.5s after click.
  const [optimisticBulkRunning, setOptimisticBulkRunning] = useState(false)
  const { data, isLoading, refetch } = useGetCampaignLeadReview(campaignId)
  const updateSelection = useUpdateCampaignLeadReview(campaignId)
  const validateRow = useValidateContactEmails(campaignId)
  const startBulk = useStartCampaignValidationJob(campaignId)
  const activeJob = useActiveCampaignValidationJob(campaignId)
  const polledBulkRunning =
    !!activeJob.data &&
    (activeJob.data.status === 'pending' ||
      activeJob.data.status === 'in_progress')
  const isBulkRunning = polledBulkRunning || optimisticBulkRunning
  const syncLeads = useSyncCampaignLeads(bisonCampaignId, campaignId)
  const applyOverrides = useApplyEmailOverrides(bisonCampaignId)

  // Split contacts into "in sequence" and "new" for live campaigns
  const inSequenceContacts = useMemo(
    () => (data?.results ?? []).filter((c) => c.bison_lead_id != null),
    [data]
  )
  const newContacts = useMemo(
    () => (data?.results ?? []).filter((c) => c.bison_lead_id == null),
    [data]
  )
  const newIncludedCount = useMemo(
    () => newContacts.filter((c) => c.is_included).length,
    [newContacts]
  )

  async function handleAddToSequence() {
    if (!bisonCampaignId || newIncludedCount === 0) return
    setIsSyncing(true)
    try {
      await syncLeads.mutateAsync()
      // Apply overrides so new leads get custom variable values
      try {
        await applyOverrides.mutateAsync()
      } catch {
        // Apply is best-effort — leads are synced regardless
      }
      toast.success(
        `${newIncludedCount} contact${newIncludedCount !== 1 ? 's' : ''} added to sequence`
      )
      await refetch()
      setActiveTab('in-sequence')
    } catch {
      toast.error('Failed to add contacts to sequence')
    } finally {
      setIsSyncing(false)
    }
  }

  const primaryAction = (() => {
    if (
      isLiveCampaign &&
      activeTab === 'new-contacts' &&
      newIncludedCount > 0
    ) {
      return {
        label: isSyncing
          ? 'Adding to sequence...'
          : `Add ${newIncludedCount} Contact${newIncludedCount !== 1 ? 's' : ''} to Sequence`,
        onClick: handleAddToSequence,
        disabled: isSyncing,
      }
    }
    return {
      label: isLiveCampaign ? 'Continue' : 'Continue to Build Sequence',
      onClick: onNext,
      disabled: false,
    }
  })()

  // For live campaigns showing "In Sequence" tab, use inSequenceContacts
  // For "New Contacts" tab or draft campaigns, use the normal filtered results
  const activeContacts = useMemo(
    () =>
      isLiveCampaign && activeTab === 'in-sequence'
        ? inSequenceContacts
        : isLiveCampaign && activeTab === 'new-contacts'
          ? newContacts
          : (data?.results ?? []),
    [isLiveCampaign, activeTab, inSequenceContacts, newContacts, data?.results]
  )

  const filteredResults = useMemo(() => {
    const results = activeContacts
    if (filter === 'included') {
      return results.filter((contact) => contact.is_included)
    }
    if (filter === 'excluded') {
      return results.filter((contact) => !contact.is_included)
    }
    if (filter === 'flagged') {
      return results.filter((contact) => contact.qa_status === 'flagged')
    }
    if (filter === 'needs_validation') {
      return results.filter(
        (contact) => contact.qa_status === 'needs_validation'
      )
    }
    return results
  }, [activeContacts, filter])

  const columns = useMemo(
    () => [
      columnHelper.accessor('full_name', {
        header: 'Contact',
        size: 260,
        cell: (info) => (
          <div>
            <p className="text-sm font-medium text-fg truncate">
              {info.getValue()}
            </p>
            <p className="text-sm text-fg-secondary truncate">
              {info.row.original.email || 'No email'}
            </p>
          </div>
        ),
      }),
      columnHelper.display({
        id: 'company',
        header: 'Company',
        size: 220,
        cell: (info) => (
          <div className="text-sm text-fg-secondary">
            <p className="truncate">{info.row.original.company_name}</p>
            <p className="truncate">{info.row.original.title || 'No title'}</p>
          </div>
        ),
      }),
      columnHelper.accessor('qa_status', {
        header: 'QA Status',
        size: 160,
        cell: (info) => <LeadStatusBadge qaStatus={info.getValue()} />,
      }),
      columnHelper.accessor('email_validity', {
        header: 'Validation',
        size: 130,
        cell: (info) => {
          const item = info.row.original
          const loading =
            validatingContactIds.has(item.contact_id) ||
            (isBulkRunning && item.qa_status === 'needs_validation')
          return (
            <ValidationBadge validity={info.getValue()} loading={loading} />
          )
        },
      }),
      columnHelper.accessor('qa_reason', {
        header: 'Reason',
        size: 320,
        cell: (info) => (
          <span className="text-sm text-fg-secondary">
            {info.getValue() || '-'}
          </span>
        ),
      }),
      columnHelper.display({
        id: 'inclusion',
        header: 'Included',
        size: 100,
        cell: (info) => (
          <span
            className={cn(
              'inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize',
              info.row.original.is_included
                ? 'bg-success-bg-subtle text-success-strong'
                : 'bg-surface-alt text-fg-secondary'
            )}
          >
            {info.row.original.is_included ? 'Included' : 'Excluded'}
          </span>
        ),
      }),
      // Hide actions for live campaigns viewing "In Sequence" tab
      ...(isLiveCampaign && activeTab === 'in-sequence'
        ? []
        : [
            columnHelper.display({
              id: 'actions',
              header: 'Actions',
              size: 140,
              meta: {
                isEditable: false,
                headerDisableOrdering: true,
                isLastColumn: true,
                rightAlign: true,
              },
              cell: (info: { row: { original: CampaignLeadReviewItem } }) => {
                const item = info.row.original
                let content: ReactNode
                if (item.qa_status === 'rejected') {
                  content = (
                    <span className="text-sm font-medium text-fg-secondary">
                      Blocked
                    </span>
                  )
                } else if (item.qa_status === 'needs_validation') {
                  const isThisRowValidating = validatingContactIds.has(
                    item.contact_id
                  )
                  const showSpinner = isThisRowValidating || isBulkRunning
                  content = (
                    <FbButton
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={showSpinner}
                      onClick={() => {
                        setValidatingContactIds((prev) => {
                          const next = new Set(prev)
                          next.add(item.contact_id)
                          return next
                        })
                        validateRow.mutate([item.contact_id], {
                          onSettled: () =>
                            setValidatingContactIds((prev) => {
                              const next = new Set(prev)
                              next.delete(item.contact_id)
                              return next
                            }),
                          onError: () =>
                            toast.error('Validation failed — try again'),
                        })
                      }}
                    >
                      {showSpinner ? (
                        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                          <PiCircleNotch className="h-3 w-3 shrink-0 animate-spin" />
                          Validating
                        </span>
                      ) : (
                        'Validate'
                      )}
                    </FbButton>
                  )
                } else {
                  content = (
                    <FbButton
                      type="button"
                      variant={item.is_included ? 'outline' : 'secondary'}
                      size="sm"
                      className={
                        item.is_included ? 'hover:!bg-white' : undefined
                      }
                      disabled={updateSelection.isPending}
                      onClick={() =>
                        updateSelection.mutate({
                          contact_ids: [item.contact_id],
                          is_included: !item.is_included,
                        })
                      }
                    >
                      {item.is_included ? 'Exclude' : 'Include'}
                    </FbButton>
                  )
                }
                return (
                  <div className="flex w-full justify-center">{content}</div>
                )
              },
            }),
          ]),
    ],
    [
      updateSelection,
      isLiveCampaign,
      activeTab,
      validateRow,
      isBulkRunning,
      validatingContactIds,
    ]
  )

  if (isLoading || !data) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-fg-secondary">
        Loading contacts...
      </div>
    )
  }

  const { summary } = data
  return (
    <div className="flex h-full flex-col">
      <WizardStepHeader
        stepNumber={stepNumber}
        totalSteps={totalSteps}
        title={isLiveCampaign ? 'Campaign Contacts' : 'Review Contacts'}
        description={
          isLiveCampaign
            ? 'View contacts in the sequence and add new contacts.'
            : 'Choose which contacts should be included before previewing emails.'
        }
      />

      <div className="flex min-h-0 flex-1 flex-col rounded-bl-2xl border-b border-l border-line bg-surface-bright px-8 py-6">
        <div className="mx-auto flex min-h-0 w-full max-w-[1200px] flex-1 flex-col gap-6">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <SummaryCard
              label="Approved"
              value={summary.approved}
              tone="approved"
            />
            <span className="h-6 w-px shrink-0 self-center bg-line" />
            <SummaryCard
              label="Flagged"
              value={summary.flagged}
              tone="flagged"
              tooltip="Flagged contacts can still be included, but they may have recent campaign activity or another signal worth reviewing first."
            />
            <span className="h-6 w-px shrink-0 self-center bg-line" />
            <SummaryCard
              label="Needs validation"
              value={summary.needs_validation}
              tone="needs_validation"
              tooltip="These contacts have at least one email we haven't yet verified with our deliverability check. Validate them before they can be included."
            />
            <span className="h-6 w-px shrink-0 self-center bg-line" />
            <SummaryCard
              label="Rejected"
              value={summary.rejected}
              tone="rejected"
              tooltip="Rejected contacts are excluded by default because they are not safe to send to, such as bounced, unsubscribed, or inactive emails, or contacts on a do-not-contact list."
            />
            <span className="h-6 w-px shrink-0 self-center bg-line" />
            <SummaryCard
              label="Missing"
              value={
                campaign
                  ? Math.max(
                      0,
                      campaign.contact_company_count -
                        campaign.companies_with_contacts
                    )
                  : 0
              }
              tone={
                campaign &&
                campaign.contact_company_count -
                  campaign.companies_with_contacts >
                  0
                  ? 'flagged'
                  : 'neutral'
              }
              tooltip="Targets in this campaign that have no contacts yet. Request contacts for them or remove the company from the campaign before launch."
            />
            <span className="h-6 w-px shrink-0 self-center bg-line" />
            <SummaryCard
              label="Included"
              value={summary.included}
              tone="neutral"
            />
          </div>

          {isLiveCampaign && (
            <div className="flex gap-1 rounded-lg bg-surface-alt p-1">
              <button
                type="button"
                onClick={() => setActiveTab('in-sequence')}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  activeTab === 'in-sequence'
                    ? 'bg-white text-fg shadow-sm'
                    : 'text-fg-secondary hover:text-fg'
                )}
              >
                In Sequence ({inSequenceContacts.length})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('new-contacts')}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  activeTab === 'new-contacts'
                    ? 'bg-white text-fg shadow-sm'
                    : 'text-fg-secondary hover:text-fg'
                )}
              >
                New Contacts
                {newContacts.length > 0 && (
                  <span className="ml-1.5 rounded-full bg-brand px-1.5 py-0.5 text-xxs text-white">
                    {newContacts.length}
                  </span>
                )}
              </button>
            </div>
          )}

          <TableLinkStyleProvider variant="plain">
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-line bg-surface shadow-sm">
              {summary.total === 0 ? (
                <div className="px-6 py-12 text-center">
                  <h2 className="text-base font-semibold text-fg">
                    No contacts yet.
                  </h2>
                  <p className="mx-auto mt-2 max-w-xl text-sm text-fg-secondary">
                    Add contacts to this campaign to review recipients and
                    preview the sequence with real leads.
                  </p>
                </div>
              ) : (
                <>
                  <div className="px-4 pt-4">
                    <h2 className="text-base font-semibold text-fg">
                      Campaign Contacts
                    </h2>
                    <p className="mt-1 text-sm text-fg-secondary">
                      Approved and flagged contacts are included by default.
                      Rejected contacts are blocked and cannot be included.
                    </p>
                  </div>
                  <div className="flex items-center justify-between gap-4 border-b border-line px-4 py-3">
                    <PillTabs<LeadReviewFilter>
                      idPrefix="campaign-review-leads"
                      aria-label="Filter contacts"
                      activeKey={filter}
                      onChange={setFilter}
                      className="w-fit"
                      tabs={[
                        {
                          key: 'included',
                          title: filterTabTitle('Included', summary.included),
                        },
                        {
                          key: 'excluded',
                          title: filterTabTitle('Excluded', summary.excluded),
                        },
                        {
                          key: 'flagged',
                          title: filterTabTitle('Flagged', summary.flagged),
                        },
                        {
                          key: 'needs_validation',
                          title: filterTabTitle(
                            'Needs validation',
                            summary.needs_validation
                          ),
                        },
                        {
                          key: 'all',
                          title: filterTabTitle('All', summary.total),
                        },
                      ]}
                    />
                    <p className="shrink-0 text-sm text-fg-secondary">
                      {filteredResults.length} shown
                    </p>
                  </div>
                  {filter === 'needs_validation' &&
                    summary.needs_validation > 0 && (
                      <div className="border-b border-line px-4 py-3">
                        <Alert
                          color="info"
                          icon={null}
                          description={
                            <div className="flex items-center justify-between gap-3">
                              {isBulkRunning && activeJob.data ? (
                                <span className="inline-flex items-center gap-2 whitespace-nowrap">
                                  <PiCircleNotch className="h-4 w-4 shrink-0 animate-spin" />
                                  Validating {activeJob.data.processed_count} /{' '}
                                  {activeJob.data.contact_count}
                                </span>
                              ) : (
                                <>
                                  <span>
                                    {summary.needs_validation} contact
                                    {summary.needs_validation !== 1
                                      ? 's'
                                      : ''}{' '}
                                    need
                                    {summary.needs_validation === 1
                                      ? 's'
                                      : ''}{' '}
                                    validation before they can be included.
                                  </span>
                                  <button
                                    type="button"
                                    disabled={
                                      isBulkRunning || startBulk.isPending
                                    }
                                    onClick={() => {
                                      setOptimisticBulkRunning(true)
                                      startBulk.mutate(undefined, {
                                        onSuccess: async () => {
                                          // Hold the spinner until the
                                          // lead-review refetch returns
                                          // post-worker data, so the table
                                          // flips buckets simultaneously with
                                          // the spinner clearing. Brief delay
                                          // first to give the Celery worker
                                          // time to write its verdicts.
                                          await new Promise((r) =>
                                            setTimeout(r, 1500)
                                          )
                                          await queryClient.refetchQueries({
                                            queryKey: [
                                              'campaign-lead-review',
                                              campaignId,
                                            ],
                                          })
                                          setOptimisticBulkRunning(false)
                                        },
                                        onError: (err: unknown) => {
                                          setOptimisticBulkRunning(false)
                                          const e = err as {
                                            response?: { status?: number }
                                          }
                                          if (e?.response?.status === 409) {
                                            toast.info('Already validating')
                                          } else {
                                            toast.error(
                                              'Failed to start validation'
                                            )
                                          }
                                        },
                                      })
                                    }}
                                    className="shrink-0 whitespace-nowrap font-medium underline underline-offset-2 hover:no-underline disabled:opacity-50"
                                  >
                                    Validate all ({summary.needs_validation})
                                  </button>
                                </>
                              )}
                            </div>
                          }
                        />
                      </div>
                    )}
                  <DataTableContainer className="min-h-0 flex-1">
                    <DataTable
                      tableKey="campaign-review-leads-table"
                      data={filteredResults}
                      columns={columns}
                      loading={isLoading || updateSelection.isPending}
                      isPaginationEnabled={false}
                      emptyTableText={EMPTY_STATE_BY_FILTER[filter]}
                      emptyStateMinHeight="min-h-[280px]"
                    />
                  </DataTableContainer>
                </>
              )}
            </div>
          </TableLinkStyleProvider>
        </div>
      </div>

      <WizardStepFooter>
        <FbButton
          type="button"
          variant="outline"
          onClick={onBack}
          className={`${WIZARD_BUTTON_MIN_W} justify-center gap-2`}
        >
          <PiCaretLeft className="size-4" />
          {isLiveCampaign ? 'Back to Dashboard' : 'Back'}
        </FbButton>
        <FbButton
          type="button"
          className={`${WIZARD_BUTTON_MIN_W} justify-center gap-2`}
          onClick={primaryAction.onClick}
          disabled={primaryAction.disabled}
        >
          {primaryAction.label}
          <PiCaretRight className="size-4" />
        </FbButton>
      </WizardStepFooter>
    </div>
  )
}

export function WizardReviewLeadsStep(props: Props) {
  return (
    <ColumnsStoreProvider tableKey="campaign-review-leads-table">
      <DataTableProvider tableKey="campaign-review-leads-table">
        <WizardReviewLeadsStepContent {...props} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
